import { useState } from 'react';
import { X, Circle } from '@phosphor-icons/react';
import { Button } from '../ui/button';

const LINES = [
  [0, 1, 2], [3, 4, 5], [6, 7, 8],
  [0, 3, 6], [1, 4, 7], [2, 5, 8],
  [0, 4, 8], [2, 4, 6]
];

const winnerOf = (board) => {
  for (const [a, b, c] of LINES) {
    if (board[a] && board[a] === board[b] && board[a] === board[c]) return board[a];
  }
  return board.every(Boolean) ? 'draw' : null;
};

const minimax = (board, isAi) => {
  const result = winnerOf(board);
  if (result === 'O') return { score: 1 };
  if (result === 'X') return { score: -1 };
  if (result === 'draw') return { score: 0 };

  const moves = [];
  board.forEach((cell, i) => {
    if (cell) return;
    const next = [...board];
    next[i] = isAi ? 'O' : 'X';
    moves.push({ index: i, score: minimax(next, !isAi).score });
  });

  return isAi
    ? moves.reduce((best, m) => (m.score > best.score ? m : best), { score: -Infinity })
    : moves.reduce((best, m) => (m.score < best.score ? m : best), { score: Infinity });
};

export function TicTacToe({ onScore }) {
  const [board, setBoard] = useState(Array(9).fill(null));
  const [streak, setStreak] = useState(0);
  const [status, setStatus] = useState('Your turn — you are X');

  const finish = (result) => {
    if (result === 'X') {
      const next = streak + 1;
      setStreak(next);
      onScore(next);
      setStatus('Incredible — you beat the AI!');
    } else if (result === 'O') {
      setStreak(0);
      setStatus('AI wins this one. Streak reset.');
    } else {
      setStatus("It's a draw — the AI is unbeatable, a draw is a win.");
    }
  };

  const play = (index) => {
    if (board[index] || winnerOf(board)) return;
    const afterPlayer = [...board];
    afterPlayer[index] = 'X';
    const playerResult = winnerOf(afterPlayer);
    if (playerResult) {
      setBoard(afterPlayer);
      finish(playerResult);
      return;
    }
    const aiMove = minimax(afterPlayer, true).index;
    const afterAi = [...afterPlayer];
    if (aiMove !== undefined) afterAi[aiMove] = 'O';
    setBoard(afterAi);
    const aiResult = winnerOf(afterAi);
    if (aiResult) finish(aiResult);
    else setStatus('Your turn');
  };

  const reset = () => {
    setBoard(Array(9).fill(null));
    setStatus('Your turn — you are X');
  };

  return (
    <div className="space-y-4" data-testid="game-tic-tac-toe">
      <div className="flex items-center justify-between text-sm">
        <span>Wins vs AI: <span className="text-amber-300 tabular-nums" data-testid="ttt-streak">{streak}</span></span>
        <span className="text-xs text-muted-foreground">Unbeatable minimax AI</span>
      </div>

      <div className="grid grid-cols-3 gap-2" data-testid="ttt-board">
        {board.map((cell, i) => (
          <button
            key={i}
            type="button"
            onClick={() => play(i)}
            className="aspect-square rounded-lg border border-white/10 bg-white/5 hover:border-amber-400/40 flex items-center justify-center transition-colors"
            data-testid={`ttt-cell-${i}`}
          >
            {cell === 'X' && <X size={34} weight="bold" className="text-amber-300" />}
            {cell === 'O' && <Circle size={30} weight="bold" className="text-rose-400" />}
          </button>
        ))}
      </div>

      <p className="text-center text-sm text-muted-foreground" data-testid="ttt-status">{status}</p>
      <Button variant="outline" className="rounded-full w-full" onClick={reset} data-testid="ttt-reset">New round</Button>
    </div>
  );
}
